import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { DetailsList, PACaseCreateForm, PACreateErrorDisplay, PAQuestionnaireForm, QuestionnaireFormContent } from "@/components";
import { createPACaseAPI, getQuestionnairesAPI, validatePACaseAPI } from "@/api/get";
import { Heading, PageTitle, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { PACase } from "@/api/interfaces";
import { PACaseDraft } from "@/api/interfaces/PACaseDraft";
import { DispensingSite, ReviewType } from "capitalrx-types";
import { Button, Buttons, Spinner, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { ValidateResultType } from "@/api/interfaces/PACase";
import { Dropdown, Form, SubmitButton, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";
import { getQuestionnaireResponses, getThemeButton, getThemeURL, resolvePACaseCreateError, stripPhoneFormatting } from "@/utils";
import { ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { useNavigate } from "react-router";


interface PACaseDraftQuestionnaireProps {
  paCaseDraft: PACaseDraft,
}

// match result from test claim to review type expected by questionnaire/recommend
type TestClaimResult = Omit<PACase.PACaseValidateResponse, "result"> & {
  result: ReviewType;
};

const getReviewType = (result?: ValidateResultType): ReviewType => {
  if (result === ValidateResultType.Nonformulary) {
    return "non_formulary_exception";
  }
  if (result === ValidateResultType.StepTherapyException) {
    return "step_therapy_exception";
  }
  if (result === ValidateResultType.PARequired) {
    return "prior_authorization_exception";
  }
  return "no_review_needed";
};

export const PACaseDraftQuestionnaire: FC<PACaseDraftQuestionnaireProps> = (props: PACaseDraftQuestionnaireProps) => {
  const { primary_color } = useContext(ThemeSelectorContext);
  const navigate = useNavigate();
  const showAlert = useAlert();
  const paCaseDraft = props.paCaseDraft;
  const [loading, setLoading] = useState<boolean>(true);
  const [questionnaires, setQuestionnaires] = useState(null);
  const [testClaimResult, setTestClaimResult] = useState<TestClaimResult | null>(null);
  const [dispensingSite, setDispensingSite] = useState<DispensingSite | null>(null);
  const [facilityNPI, setFacilityNPI] = useState<string>(null);
  const form = useForm<PACaseCreateForm>({
    defaultValues: {
      dispensing_site: props.paCaseDraft.dispensing_site as DispensingSite|| "pharmacy",
    },
  });
  const questionnaireForm = useForm<PAQuestionnaireForm>();

  const { six_six_twelve_columns, three_three_zero_columns } = useResponsive({
    six_six_twelve_columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
    three_three_zero_columns: {
      desktop: 3,
      tablet: 3,
      mobile: 0,
    },
  });

  useEffect(() => {
    const runTestClaim = async () => {
      try {
        setLoading(true);

        const paCaseValidateResponse: PACase.PACaseValidateResponse = await validatePACaseAPI({
          external_member_id: paCaseDraft.external_member_id,
          member_first_name: paCaseDraft.patient_first_name,
          member_last_name: paCaseDraft.patient_last_name,
          member_date_of_birth: paCaseDraft.patient_date_of_birth,
          ndc: paCaseDraft.ndc,
          npi: paCaseDraft.prescriber_npi,
          quantity_dispensed: paCaseDraft.quantity_prescribed, // XXX: ehhhh
          days_supply: paCaseDraft.days_supply,
          icd_10: paCaseDraft.diagnosis_codes,
          daw_code: String(paCaseDraft.daw),
        });

        if (paCaseValidateResponse.result === ValidateResultType.DrugNotFound) {
          throw new Error(`Drug not found for NDC ${paCaseDraft.ndc}`);
        }

        setTestClaimResult({
          ...paCaseValidateResponse,
          result: getReviewType(paCaseValidateResponse.result),
        });

        setLoading(false);

      } catch (error) {
        console.error(error);
        showAlert({ type: "error", style: "banner", message: "Failed to load clinical criteria questions" });
        setLoading(false);
      }
    };

    runTestClaim();
  }, [showAlert]);

  const fetchQuestionnaires = useCallback(async () => {
    if (!dispensingSite || !testClaimResult || testClaimResult.result === "no_review_needed") {
      return;
    }

    try {
      const result = await getQuestionnairesAPI(
        {
          member_client_id: testClaimResult.test_claim.client_id,
          external_account_id: testClaimResult.test_claim.external_account_id,
          member_group_id: testClaimResult.test_claim.group_id,
          external_member_id: paCaseDraft.external_member_id,
          person_code: testClaimResult.test_claim.person_code,

          drug_ndc: paCaseDraft.ndc,
          claim_compound_code: testClaimResult.test_claim.compound_code,
          claim_reject_codes: testClaimResult.test_claim.reject_codes,
          programs: testClaimResult.test_claim.programs,
          review_types: [testClaimResult.result],
          dispensing_site: dispensingSite,
          formulary_identifier: testClaimResult.test_claim.formulary_identifier,
        },
      );
      setQuestionnaires(result);
    } catch (error) {
      console.error(error);
      showAlert({ type: "error", style: "banner", message: "Failed to load clinical criteria questions" });
    }
  }, [dispensingSite, paCaseDraft.external_member_id, paCaseDraft.ndc, showAlert, testClaimResult]);

  // set dispensing site from draft data if it is valid, and fetch questionnaires if it is pharmacy
  useEffect(() => {
    if (paCaseDraft.dispensing_site === "pharmacy" || paCaseDraft.dispensing_site === "prescriber_administered") {
      setDispensingSite(paCaseDraft.dispensing_site);

      if (paCaseDraft.dispensing_site === "pharmacy") {
        fetchQuestionnaires();
      }
    }
  }, [fetchQuestionnaires, paCaseDraft.dispensing_site]);

  const caseDraftDetails = useMemo(() => {
    if (!paCaseDraft) {
      return {};
    }

    return {
      "Prescriber": `${paCaseDraft.prescriber_first_name} ${paCaseDraft.prescriber_last_name}`,
      "Prescriber NPI": paCaseDraft.prescriber_npi,
      "Alternate Fax Number": paCaseDraft.alternate_contact_fax_number,
      "Urgent": paCaseDraft.urgent ? "Yes" : "No",
      "Member ID": paCaseDraft.external_member_id,
      "Drug": testClaimResult?.test_claim.drug_name || "",
      "NDC": paCaseDraft.ndc,
      "Dispensing Site": dispensingSite,
      "DAW Code": paCaseDraft.daw,
    };
  }, [dispensingSite, paCaseDraft, testClaimResult]);

  const [createError, setCreateError] = useState<{
    message: string;
    errorCode?: string;
    details?: Record<string, string>;
  } | null>(null);

  const goToCase = useCallback((caseId: number) => {
    const formValues = form.getValues();
    const params = {
      case_id: caseId.toString(),
      member_date_of_birth: formValues.member_date_of_birth,
      prescriber_npi: formValues.npi,
      external_member_id: formValues.external_member_id,
    };
    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, params[key]);
      }
    }
    navigate(`${getThemeURL("PA_CASE")}?${encodedParams.toString()}`);
  }, [navigate]);

  const submitPriorAuthCase = useCallback(async () => {
    setCreateError(null);
    logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.submit);

    const formVals = form.getValues();

    const sanitizedData = {
      client_id: "HUBLCN",
      external_member_id: "HUBLCN001",
      member_first_name: paCaseDraft.patient_first_name,
      member_last_name: paCaseDraft.patient_last_name,
      member_date_of_birth: paCaseDraft.patient_date_of_birth,
      person_code: "01",
      is_urgent: paCaseDraft.urgent,
      review_type: formVals.review_type,
      drug_ndc: paCaseDraft.ndc,
      drug_quantity: paCaseDraft.quantity_prescribed,
      drug_days_supply: paCaseDraft.days_supply,
      drug_name: testClaimResult?.test_claim.drug_name || "",
      drug_directions_for_use: paCaseDraft.directions_for_use,
      icd_10: paCaseDraft.diagnosis_codes,
      provider_npi: paCaseDraft.prescriber_npi,

      // provider_phone: paCaseDraft.,
      provider_fax: paCaseDraft.prescriber_fax_number,
      provider_first_name: paCaseDraft.prescriber_first_name,
      provider_last_name: paCaseDraft.prescriber_last_name,

      provider_email: paCaseDraft.prescriber_email,
      /* provider_address: paCaseDraft.pres,
         provider_address_2: paCaseDraft.provider_address_2,
         provider_city: paCaseDraft.provider_city,
         provider_state: paCaseDraft.provider_state,
         provider_zip: paCaseDraft.provider_zip, */

      alternate_fax_number: stripPhoneFormatting(paCaseDraft.alternate_contact_fax_number),
      dispensing_site: dispensingSite,
      facility_npi: facilityNPI,
      questionnaire_responses: formVals.questionnaire_responses,
      daw_code: paCaseDraft.daw,
      renewal_code: undefined,
    };

    try {
      const { case_id: caseId } = await createPACaseAPI(sanitizedData);
      goToCase(caseId);

    } catch (error) {
      const resolution = await resolvePACaseCreateError(error);
      if (resolution.caseId !== null) {
        goToCase(resolution.caseId);
        return;
      }

      setCreateError(resolution.error);
      showAlert({ type: "error", style: "banner", message: resolution.error.message });

      logEvent(ANALYTIC_EVENTS.ERRORS[resolution.error.analyticsEvent] || ANALYTIC_EVENTS.ERRORS.caseCreate);

      window.scrollTo({ top: 0, behavior: "smooth" });
    }

  }, [caseDraftDetails, dispensingSite, facilityNPI, goToCase, paCaseDraft, showAlert, testClaimResult, form]);

  const formSubmitted = useCallback(async (data: PAQuestionnaireForm) => {
      const responses = getQuestionnaireResponses(data, questionnaires);
      form.setValue("provider_first_name", data.provider_first_name as string);
      form.setValue("provider_last_name", data.provider_last_name as string);
      form.setValue("provider_phone", data.provider_phone as string);
      form.setValue("provider_fax", data.provider_fax as string);
      form.setValue("provider_email", data.provider_email as string);
      form.setValue("provider_address", data.provider_address as string);
      form.setValue("provider_address_2", data.provider_address_2 as string);
      form.setValue("provider_city", data.provider_city as string);
      form.setValue("provider_state", data.provider_state as string);
      form.setValue("provider_zip", data.provider_zip as string);
      form.setValue("questionnaire_responses", responses);
      await submitPriorAuthCase(); // Or something that receives the form data.
    }, [questionnaires]);

    const handleFacilityNPIChange = (value: string) => {
      const sanitizedNPI = value.replace(/\D/g, ""); // Remove non-digit characters
      setFacilityNPI(sanitizedNPI);
    };


  return (
    <>
      {loading && <Stack alignment="center"><Spinner size="large" /></Stack>}
      {!loading &&
        <Stack spacing="large" style={{ width: "100%" }}>
          <Grid>
            <Grid.Span columns={three_three_zero_columns}/>
            <Grid.Span columns={six_six_twelve_columns}>
              <Stack spacing="large">
                <PageTitle testId="test__prior_authorization_case_draft_questionnaire_page_title">New Prior Authorization Case</PageTitle>
                <Stack>
                  <Heading testId="test__prior_authorization_case_draft_questionnaire_page_case_details_heading">Case</Heading>
                  <DetailsList testId="test__prior_authorization_case_draft_questionnaire_page_case_details_list" data={caseDraftDetails} />
                </Stack>
                {testClaimResult?.result !== "no_review_needed" && questionnaires === null && (
                  <Shelf alignment="bottom">
                    <Dropdown
                      label="Dispensing Site"
                      value={dispensingSite}
                      onChange={(value) => setDispensingSite(value as DispensingSite)}
                      style={{ width: 200 }}
                  >
                    <Dropdown.Option value="pharmacy">Pharmacy</Dropdown.Option>
                    <Dropdown.Option value="prescriber_administered">Prescriber Administered</Dropdown.Option>
                  </Dropdown>
                  {dispensingSite === "prescriber_administered" && (
                    <Textbox
                      label="Dispensing Provider NPI"
                      value={facilityNPI}
                      onChange={(value) => handleFacilityNPIChange(value)}
                      style={{ width: 200 }}
                    />
                  )}
                  <Button appearance={Buttons.Functional.Primary.Large} onClick={fetchQuestionnaires}>Submit</Button>
                </Shelf>)}
              </Stack>
              {testClaimResult?.result === "no_review_needed" && (
                <span>No Prior Authorization required.</span>
              )}
              {questionnaires !== null && !questionnaires?.results?.length && (
                <span>No Clinical Criteria Questions necessary</span>
              )}
              {questionnaires !== null && questionnaires.results !== null && (
                <>
                  {createError && <PACreateErrorDisplay error={createError} />}

                  <Form form={questionnaireForm} onSubmit={formSubmitted}>
                    <Stack>
                      <Subtitle>Clinical Criteria Questions</Subtitle>
                      <Stack.Spacer />
                      {questionnaires !== null && questionnaires.results?.map(({ questionnaire }) => {
                        return (
                          <QuestionnaireFormContent
                            key={questionnaire.id}
                            answersOpen={true}
                            questionnaire={questionnaire}
                          />
                        );
                      })}
                    </Stack>
                    <SubmitButton appearance={getThemeButton("primary", primary_color)}>
                      Submit
                    </SubmitButton>
                  </Form>
                </>
              )}
            </Grid.Span>
          </Grid>
        </Stack>
      }
    </>
  );
};
